import { Api, ApiError } from "./api";
import { AppConfig, completeSignIn, signIn, signOut } from "./auth";
import { h, replace } from "./dom";
import { fromHash, inApp, navigate, RELOAD } from "./nav";
import type { Me } from "./types";
import { account, accounts } from "./views/accounts";
import { agentOrderForm } from "./views/agent-order";
import { commissions } from "./views/commissions";
import { documentView } from "./views/document";
import { documents } from "./views/documents";
import { inquiries } from "./views/inquiries";
import { draft } from "./views/draft";
import { exceptionView } from "./views/exception";
import { home } from "./views/home";
import { invoices } from "./views/invoices";
import { mail } from "./views/mail";
import { order } from "./views/order";
import { orders } from "./views/orders";
import { products } from "./views/products";
import { quickbooks } from "./views/quickbooks";
import { releases } from "./views/releases";
import { vendorPo, vendorPoByNumber } from "./views/vendor-po";

// The Tare app (D-23, docs/design/app.md): the shell (header, mode badge, who's signed in) and the screens, by the
// address's path (CloudFront serves the app's page for any path that isn't a file; nav.ts moves between them):
//   /                         Home, what needs you
//   /orders?customer=&status= customer POs
//   /order/<id>               one customer PO
//   /mail?decision=&days=     every email the outbound guard sent, drafted or stopped
//   /releases?customer=&from=&weeks=  releases by day, and cover on open POs
//   /invoices?supplier=&show=  supplier invoices, how each matched, when due
//   /commissions?customer=&supplier=&status=  Broker/Agent orders and their commission
//   /products?customer=&q=    a customer's products, requirements and (with costs.read) prices
//   /accounts?q=&kind=        customers, suppliers and others; /accounts/<code> one of them
//   /documents?pile=&show=&days=  mail received and its attachments, known and unknown senders apart
//   /inquiries?days=&spam=    the website's contact form
//   /quickbooks?show=         entries the platform makes in QuickBooks, for an Owner to approve (phase 5)
//   /commissions/new          record a Broker/Agent order by hand
//   /vendor-po/<po>/<vpo>     one vendor PO (to approve, or as it stands)
//   /vendor-po/<number>       the same, by its number (TG-S0001): what an email knows
//   /draft/<id>               one outbound draft
//   /document/<id>            one document read from mail
//   /exception/<id>           one exception
// An old #/… link is turned into its path on arrival.

type Screen = (api: Api, me: Me) => Promise<HTMLElement>;
type Section = "home" | "orders" | "releases" | "invoices" | "commissions" | "products" | "accounts" | "documents" | "inquiries" | "quickbooks" | "mail";

function screenFor(pathname: string, query: string): { screen: Screen; section: Section } {
  const p = pathname.replace(/^\/+|\/+$/g, "").split("/");
  const n = (i: number) => Number.parseInt(p[i] ?? "", 10);
  if (p[0] === "orders") return { screen: (a) => orders(a, new URLSearchParams(query ?? "")), section: "orders" };
  if (p[0] === "releases") return { screen: (a) => releases(a, new URLSearchParams(query ?? "")), section: "releases" };
  if (p[0] === "invoices") return { screen: (a) => invoices(a, new URLSearchParams(query ?? "")), section: "invoices" };
  if (p[0] === "commissions" && p[1] === "new") return { screen: (a, m) => agentOrderForm(a, m), section: "commissions" };
  if (p[0] === "commissions") return { screen: (a, m) => commissions(a, m, new URLSearchParams(query ?? "")), section: "commissions" };
  if (p[0] === "products") return { screen: (a, m) => products(a, m, new URLSearchParams(query ?? "")), section: "products" };
  if (p[0] === "accounts" && p[1]) return { screen: (a) => account(a, decodeURIComponent(p[1])), section: "accounts" };
  if (p[0] === "accounts") return { screen: (a) => accounts(a, new URLSearchParams(query ?? "")), section: "accounts" };
  if (p[0] === "documents") return { screen: (a) => documents(a, new URLSearchParams(query ?? "")), section: "documents" };
  if (p[0] === "inquiries") return { screen: (a) => inquiries(a, new URLSearchParams(query ?? "")), section: "inquiries" };
  if (p[0] === "quickbooks") return { screen: (a, m) => quickbooks(a, m, new URLSearchParams(query ?? "")), section: "quickbooks" };
  if (p[0] === "mail") return { screen: (a) => mail(a, new URLSearchParams(query ?? "")), section: "mail" };
  if (p[0] === "order" && n(1) > 0) return { screen: (a, m) => order(a, m, n(1), new URLSearchParams(query)), section: "orders" };
  if (p[0] === "vendor-po" && n(1) > 0 && n(2) > 0) {
    return { screen: (a, m) => vendorPo(a, m, n(1), n(2), new URLSearchParams(query)), section: "orders" };
  }
  if (p[0] === "vendor-po" && /^[A-Z]{2}-[A-Z0-9-]{1,20}$/i.test(p[1] ?? "")) {
    return { screen: (a, m) => vendorPoByNumber(a, m, decodeURIComponent(p[1]).toUpperCase()), section: "orders" };
  }
  if (p[0] === "draft" && n(1) > 0) return { screen: (a, m) => draft(a, m, n(1)), section: "home" };
  if (p[0] === "document" && n(1) > 0) return { screen: (a, m) => documentView(a, m, n(1)), section: "home" };
  if (p[0] === "exception" && n(1) > 0) return { screen: (a, m) => exceptionView(a, m, n(1)), section: "home" };
  return { screen: home, section: "home" };
}

function header(cfg: AppConfig, me: Me | null, section: Section): HTMLElement {
  const sim = me?.mode !== "LIVE";
  const out = h("button", { type: "button", class: "signout" }, "Sign out");
  out.addEventListener("click", () => signOut(cfg));
  // On a phone the bar keeps TARE, the mode badge (always in sight) and a Menu button; the links, the person and Sign
  // out move into a panel the button opens (styles.css, under 720px). Each screen draws a fresh header, so it's closed.
  const menu = h("div", { id: "app-menu", class: "app-menu" },
    h("nav", { "aria-label": "Main", class: "app-nav" },
      me ? [
        h("a", { href: "/", class: "navlink", "aria-current": section === "home" ? "page" : null }, "Home"),
        h("a", { href: "/orders", class: "navlink", "aria-current": section === "orders" ? "page" : null }, "Orders"),
        me.permissions?.includes("orders.read")
          ? h("a", { href: "/releases", class: "navlink", "aria-current": section === "releases" ? "page" : null }, "Releases") : null,
        me.permissions?.includes("invoices.read")
          ? h("a", { href: "/invoices", class: "navlink", "aria-current": section === "invoices" ? "page" : null }, "Invoices") : null,
        me.permissions?.includes("commissions.read")
          ? h("a", { href: "/commissions", class: "navlink", "aria-current": section === "commissions" ? "page" : null }, "Commissions") : null,
        me.permissions?.includes("products.read")
          ? h("a", { href: "/products", class: "navlink", "aria-current": section === "products" ? "page" : null }, "Products") : null,
        me.permissions?.includes("orders.read")
          ? h("a", { href: "/accounts", class: "navlink", "aria-current": section === "accounts" ? "page" : null }, "Accounts") : null,
        me.permissions?.includes("documents.read")
          ? h("a", { href: "/documents", class: "navlink", "aria-current": section === "documents" ? "page" : null }, "Documents") : null,
        me.permissions?.includes("orders.read")
          ? h("a", { href: "/inquiries", class: "navlink", "aria-current": section === "inquiries" ? "page" : null }, "Inquiries") : null,
        me.permissions?.includes("invoices.read") && me.permissions?.includes("costs.read")
          ? h("a", { href: "/quickbooks", class: "navlink", "aria-current": section === "quickbooks" ? "page" : null }, "QuickBooks") : null,
        me.permissions?.includes("drafts.read")
          ? h("a", { href: "/mail", class: "navlink", "aria-current": section === "mail" ? "page" : null }, "Mail") : null,
      ] : null),
    h("div", { class: "who" },
      cfg.environment !== "prod" ? h("span", { class: "env" }, cfg.environment) : null,
      me ? h("span", null, me.name) : null,
      me ? out : null));
  const toggle = h("button", { type: "button", class: "menu-toggle", "aria-expanded": "false", "aria-controls": "app-menu" }, "Menu");
  toggle.addEventListener("click", () => {
    const open = menu.classList.toggle("open");
    toggle.setAttribute("aria-expanded", String(open));
  });
  return h("header", { class: "app-header" },
    h("a", { href: "/", class: "wordmark" }, "TARE"),
    me ? h("span", { class: `mode ${sim ? "mode-sim" : "mode-live"}` }, h("span", { class: "dot", "aria-hidden": "true" }), me.mode) : null,
    me ? toggle : null,
    menu);
}

async function main(): Promise<void> {
  const root = document.getElementById("app")!;
  const cfg = (await (await fetch("/config.json", { cache: "no-store" })).json()) as AppConfig;
  const shell = (me: Me | null, body: HTMLElement, section: Section = "home") =>
    replace(root, header(cfg, me, section), h("div", { class: "ticks", "aria-hidden": "true" }), h("main", { id: "main" }, body));

  const legacy = fromHash(location.hash);
  if (legacy) history.replaceState(null, "", legacy);
  try {
    await completeSignIn(cfg);
  } catch (err) {
    const again = h("button", { type: "button", class: "button button-primary" }, "Sign in again");
    again.addEventListener("click", () => void signIn(cfg));
    return shell(null, h("div", { class: "page narrow" }, h("h1", { class: "title" }, "Sign-in didn't finish"),
      h("p", { class: "lede" }, (err as Error).message), again));
  }

  const api = new Api(cfg);
  let me: Me;
  try {
    me = await api.me<Me>();
  } catch (err) {
    const status = (err as ApiError).status;
    return shell(null, h("div", { class: "page narrow" }, h("h1", { class: "title" }, "The Tare app"),
      h("p", { class: "lede" }, status === 403
        ? "You're signed in, but not as an active member of Tare's team. Ask Brian to check your account."
        : `The platform didn't answer: ${(err as Error).message}`)));
  }
  document.title = me.mode === "SIMULATION" ? "Tare (simulation)" : "Tare";

  const render = async () => {
    const { screen, section } = screenFor(location.pathname, location.search.replace(/^\?/, ""));
    shell(me, h("div", { class: "page loading", "aria-busy": "true" }, h("p", { class: "caption" }, "Weighing up…")), section);
    try {
      shell(me, await screen(api, me), section);
      window.scrollTo(0, 0);
    } catch (err) {
      shell(me, h("div", { class: "page" }, h("h1", { class: "title" }, "That didn't load"),
        h("p", { class: "lede" }, (err as Error).message), h("a", { href: "/" }, "Back to Home")), section);
    }
  };
  window.addEventListener(RELOAD, () => void render());
  window.addEventListener("popstate", () => void render());
  // A click on a link inside the app is a history step, not a page load.
  document.addEventListener("click", (e) => {
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || !inApp(a, e, location.origin, location)) return;
    e.preventDefault();
    const url = new URL(a.href);
    navigate(url.pathname + url.search);
  });
  await render();
}

void main();
